import { useState, useEffect, useRef } from 'react'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/Button'
import { useSwipe } from '@/hooks/useSwipe'

interface GalleryImage {
	src: string
	caption?: string
}

interface GalleryModalProps {
	open: boolean
	onClose: () => void
	title: string
	images: GalleryImage[]
	initialIndex?: number
}

export function GalleryModal ({
	open,
	onClose,
	title,
	images,
	initialIndex = 0
}: GalleryModalProps) {
	const [currentIndex, setCurrentIndex] = useState(0)
	const imageAreaRef = useRef<HTMLDivElement>(null)

	const total = images.length

	useEffect(() => {
		if (open) setCurrentIndex(Math.min(Math.max(initialIndex, 0), Math.max(total - 1, 0)))
	}, [open, initialIndex, total])

	const current = images[currentIndex]

	const goPrev = () => setCurrentIndex(i => (i > 0 ? i - 1 : total - 1))
	const goNext = () => setCurrentIndex(i => (i < total - 1 ? i + 1 : 0))

	useSwipe(imageAreaRef, {
		onSwipeLeft: goNext,
		onSwipeRight: goPrev,
		enabled: open && total > 1
	})

	useEffect(() => {
		if (!open || total <= 1) return

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'ArrowLeft') {
				goPrev()
			} else if (e.key === 'ArrowRight') {
				goNext()
			}
		}

		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [open, total])

	return (
		<Modal
			open={open}
			onClose={onClose}
			title={title ? `${title} - Gallery` : 'Gallery'}
			fullScreen
			spacesClass='spaces-gallery-modal'
		>
			<div ref={imageAreaRef} className='spaces-gallery-modal-image spaces:relative spaces:w-full spaces:h-full'>
				{/* Image */}
				{current && (
					<img
						src={current.src}
						alt={current.caption
							? `${current.caption} (image ${currentIndex + 1} of ${total})`
							: `${title || 'Gallery'} image ${currentIndex + 1} of ${total}`}
						draggable={false}
						className='spaces:absolute spaces:inset-0 spaces:w-full spaces:h-full spaces:object-contain'
					/>
				)}

				{/* Counter badge */}
				{total > 1 && (
					<div aria-hidden='true' className='spaces:absolute spaces:top-8 spaces:right-8 spaces:flex spaces:items-center spaces:gap-4 spaces:px-12 spaces:py-8 spaces:rounded-8 spaces:bg-black/55 spaces:text-14/16 spaces:font-default'>
						<span className='spaces:text-white'>{currentIndex + 1}</span>
						<span className='spaces:text-white/60'>/</span>
						<span className='spaces:text-white'>{total}</span>
					</div>
				)}

				{/* Nav arrows */}
				{total > 1 && (
					<>
						<div className='spaces:absolute spaces:left-8 spaces:top-1/2 spaces:-translate-y-1/2'>
							<Button
								iconOnly='CaretLeft'
								appearance='subtle'
								color='overlay'
								size='md'
								aria-label='Previous image'
								onClick={goPrev}
							/>
						</div>
						<div className='spaces:absolute spaces:right-8 spaces:top-1/2 spaces:-translate-y-1/2'>
							<Button
								iconOnly='CaretRight'
								appearance='subtle'
								color='overlay'
								size='md'
								aria-label='Next image'
								onClick={goNext}
							/>
						</div>
					</>
				)}

				{/* Caption bar — hidden from AT because the caption is already the image's alt text */}
				{current?.caption && (
					<div aria-hidden='true' className='spaces:absolute spaces:bottom-0 spaces:left-0 spaces:right-0 spaces:flex spaces:flex-col spaces:items-center spaces:justify-end'>
						<div className='spaces:h-32 spaces:w-full spaces:bg-gradient-to-b spaces:from-transparent spaces:to-black/55' />
						<div className='spaces:w-full spaces:bg-black/55 spaces:p-12 spaces:text-center'>
							<p className='spaces:text-14/16 spaces:font-default spaces:text-white'>{current.caption}</p>
						</div>
					</div>
				)}
			</div>
		</Modal>
	)
}
